import { useCallback } from 'react';
import { apiSlice } from '../apis/api';
import { setUser } from '../store/userSlice';
import { useAppDispatch } from '../store';
import { router } from '../routes/router';
import { msalInstance } from '../auth/msalInstance';
import { microsoftLogoutRequest } from '../auth/msalConfig';
import Auth from '../auth/Auth';

const isMsalUserCancelledError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'errorCode' in error &&
  error.errorCode === 'user_cancelled';

/**
 * Signs the user out the way the user menu does: the Microsoft session too when there is one,
 * then the local token, the cached API state and the user, and back to the login page. Shared with
 * the screens that have no menu to offer it - an account with no project to open has to be able
 * to leave.
 */
export const useLogout = (): (() => Promise<void>) => {
  const dispatch = useAppDispatch();
  return useCallback(async () => {
    const completeLocalSignOut = (): void => {
      dispatch(apiSlice.util.resetApiState());
      new Auth().signOut();
      dispatch(setUser(undefined));
      void router.navigate('/login');
    };

    const hasMicrosoftSession = msalInstance.getAllAccounts().length > 0;
    if (!hasMicrosoftSession) {
      completeLocalSignOut();
      return;
    }

    try {
      await msalInstance.initialize();
      await msalInstance.logoutPopup({
        ...microsoftLogoutRequest,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      });
      completeLocalSignOut();
    } catch (error: unknown) {
      if (isMsalUserCancelledError(error)) {
        // If user closes the popup, keep the current local session untouched.
        return;
      }
      completeLocalSignOut();
    }
  }, [dispatch]);
};
